import axios, { AxiosResponse } from 'axios';
import React, { createContext, useCallback, useEffect, useState } from 'react';

export interface AuthUser {
  id?: string;
  username: string;
  token: string;
  error?: { code: string; message: string };
}

export const AuthResponseCodes = [
  'success',
  'invalid_credentials',
  'user_exists',
  'not_authorized',
  'server_error',
  'invalid_token',
  'no_auth_header',
  'invalid_auth_type',
  'auth_context_not_set',
  'token_expired',
  'bad_request',
  'forbidden',
  'email_not_verified',
  'too_many_requests',
  'invalid_verify',
] as const;
export type AuthResponseCode = (typeof AuthResponseCodes)[number];

interface LoginOptions {
  username: string;
  password?: string;
  stay_signed_in?: boolean;
  accountId?: string | null;
  token?: string | null;
}

export interface AuthContextData<T extends AuthUser = AuthUser> {
  isLoading: boolean;
  user?: T | null;
  error?: AuthResponseCode;
  isSelf: (userId: string) => boolean;
  login: (options: LoginOptions) => Promise<AuthResponseCode>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  token?: string | null;
  getAuthHeader: () => string | undefined;
  signup: (username: string, password: string) => Promise<AuthResponseCode>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<AuthResponseCode>;
  forgotPassword: (options: { username: string; accountId?: string }) => Promise<AuthResponseCode>;
  resetPassword: (options: {
    username: string;
    accountId?: string;
    password: string;
    token: string;
  }) => Promise<AuthResponseCode>;
  verifyEmail: (email: string, code: string) => Promise<AuthResponseCode>;
  resendVerificationEmail: (username: string) => Promise<AuthResponseCode>;
  refresh: () => Promise<void>;
}

const emptyContext: AuthContextData = {
  isLoading: false,
  isAuthenticated: false,
  isSelf: () => false,
  login: () => Promise.reject(new Error('Not initialized')),
  logout: () => Promise.reject(new Error('Not initialized')),
  getAuthHeader: () => undefined,
  signup: (username, password) => Promise.reject(new Error('Not initialized')),
  forgotPassword: () => Promise.reject(new Error('Not initialized')),
  resetPassword: () => Promise.reject(new Error('Not initialized')),
  verifyEmail: () => Promise.reject(new Error('Not initialized')),
  resendVerificationEmail: () => Promise.reject(new Error('Not initialized')),
  refresh: () => Promise.reject(new Error('Not initialized')),
  changePassword: () => Promise.reject(new Error('Not initialized')),
};

interface AuthState<T extends AuthUser = AuthUser> {
  data: T | null;
  loading: boolean;
  error: AuthResponseCode | undefined;
}

export const AuthContext = createContext<AuthContextData>(emptyContext);

interface AuthContextProviderProps<T extends AuthUser = AuthUser> {
  children?: React.ReactNode;
  authEndpoint: string;
  onLogin?: (user: T, persist?: boolean) => void;
  onError?: (error: AuthResponseCode) => void;
  onLogout?: () => void;
  token?: string | null;
}

let tokenRefreshTimer: NodeJS.Timeout | undefined = undefined;
const tokenRefreshInterval = 12 * 60 * 60_000;

const startTokenRefreshProcess = (oldToken: string, loginFn: (token: string) => unknown) => {
  tokenRefreshTimer = setTimeout(() => {
    loginFn(oldToken);
  }, tokenRefreshInterval);
};

export function AuthContextProvider<T extends AuthUser = AuthUser>(props: AuthContextProviderProps<T>) {
  const { children, authEndpoint, onLogin, onLogout, token: oldToken, onError } = props;
  const [authState, setAuthState] = useState<AuthState<T>>({
    data: null,
    loading: oldToken !== null,
    error: undefined,
  });

  const processResponse = useCallback(
    (response?: AxiosResponse<T, any>): { code: AuthResponseCode; data: T | null } => {
      if (!response) {
        return { code: 'server_error', data: null };
      }
      let error: AuthResponseCode | undefined = undefined;
      if (response.data?.error?.code) {
        error = response.data.error.code as AuthResponseCode;
      }
      if (response.status === 401) {
        error = 'not_authorized';
      } else if (response.status === 403) {
        error = 'forbidden';
      } else if (response.status === 409) {
        if (response.data.error?.code === 'user_exists') {
          error = 'user_exists';
        } else {
          error = 'not_authorized';
        }
      } else if (response.status === 400) {
        error = 'bad_request';
      } else if (response.status === 500) {
        error = 'server_error';
      } else if (response.status !== 200) {
        error = 'server_error';
      }

      if (error) {
        if (onError) {
          onError(error);
        }
        return { code: error, data: null };
      }
      return { code: 'success', data: response.data };
    },
    [onError],
  );

  const processLoginResponse = useCallback(
    async (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      response: AxiosResponse<T, any>,
      persist?: boolean,
    ): Promise<T | null> => {
      const r = processResponse(response);
      if (r.code !== 'success') {
        return null;
      }
      if (!r.data?.username || !response.data.token) {
        return null;
      }

      if (onLogin) {
        onLogin(response.data, persist);
      }
      return response.data;
    },
    [onLogin, processResponse],
  );

  const silentLogin = useCallback(
    async (t: string): Promise<void> => {
      setAuthState((s) => ({ ...s, loading: true }));
      try {
        const response = await axios.post<T>(`${authEndpoint}/login?s=1`, {
          token: t ?? null,
        });

        startTokenRefreshProcess(response.data.token, silentLogin);
        const data = await processLoginResponse(response, true);
        setAuthState({ loading: false, error: data ? undefined : 'not_authorized', data });
      } catch (e) {
        setAuthState({ loading: false, error: 'not_authorized', data: null });
      }
    },
    [authEndpoint, processLoginResponse, authState.loading, setAuthState],
  );

  const login = useCallback(
    async (options: LoginOptions): Promise<AuthResponseCode> => {
      const { username, token, stay_signed_in: persist = true, accountId } = options;

      let userData: T | null = null;
      let result: AuthResponseCode = 'success';

      setAuthState({ loading: true, data: null, error: undefined });

      try {
        const postData: LoginOptions = {
          username,
        };
        if (options.password) {
          postData.password = options.password;
        }
        if (accountId) {
          postData.accountId = accountId;
        }
        if (token) {
          postData.token = token;
        }
        if (persist) {
          postData.stay_signed_in = true;
        }

        const response = await axios.post(`${authEndpoint}/login${token ? '?t=' + token : ''}`, postData);
        startTokenRefreshProcess(response.data.token, silentLogin);
        userData = await processLoginResponse(response, persist);
      } catch (e) {
        if (axios.isAxiosError(e) && e.response) {
          const r = processResponse(e.response);
          result = r.code;
        } else {
          result = 'server_error';
        }
      }

      setAuthState((s) => ({ loading: false, error: result !== 'success' ? result : undefined, data: userData }));

      if (onError && result !== 'success') {
        onError(result);
      }

      return result;
    },
    [authEndpoint, processLoginResponse, setAuthState, onError, silentLogin],
  );

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string): Promise<AuthResponseCode> => {
      if (!authState.data) return 'bad_request';

      let result: AuthResponseCode = 'success';
      const username = authState.data.username;

      setAuthState((s) => ({ ...s, loading: true }));

      try {
        const response = await axios.post<T>(`${authEndpoint}/change-password`, {
          username,
          oldPassword,
          newPassword,
        });
        await processLoginResponse(response, true);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const r = processResponse(e.response);
          result = r.code ?? 'not_authorized';
        } else {
          result = 'server_error';
        }
      }

      setAuthState((s) => ({ ...s, loading: false, error: result !== 'success' ? result : undefined }));

      if (onError && result !== 'success') {
        onError(result);
      }

      return result;
    },
    [authState.data, authEndpoint, onError, processResponse, setAuthState],
  );

  const forgotPassword = useCallback(
    async (options: { username: string; accountId?: string }): Promise<AuthResponseCode> => {
      const { username, accountId } = options;
      let result: AuthResponseCode = 'success';

      setAuthState((s) => ({ ...s, loading: true }));

      try {
        const postData: Record<string, string> = {
          username,
        };
        if (accountId) {
          postData.accountId = accountId;
        }

        await axios.post(`${authEndpoint}/forgot-password`, postData);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const r = processResponse(e.response);
          result = r.code ?? 'not_authorized';
        } else {
          result = 'server_error';
        }
      }
      setAuthState((s) => ({ loading: false, error: result !== 'success' ? result : undefined, data: null }));
      if (onError && result !== 'success') {
        onError(result);
      }
      return result;
    },
    [authEndpoint, onError, processResponse, setAuthState],
  );

  const resetPassword = useCallback(
    async (options: {
      username: string;
      accountId?: string;
      password: string;
      token: string;
    }): Promise<AuthResponseCode> => {
      const { username, accountId, password } = options;
      setAuthState((s) => ({ ...s, loading: true }));
      let result: AuthResponseCode = 'success';

      try {
        const postData: Record<string, string> = {
          username,
          password,
        };
        if (accountId) {
          postData.accountId = accountId;
        }

        await axios.post(`${authEndpoint}/reset-password/?token=${options.token}`, postData);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const r = processResponse(e.response);
          if (r.code !== 'success') {
            result = r.code ?? 'not_authorized';
          }
        }
      }

      setAuthState((s) => ({ ...s, loading: false, error: result !== 'success' ? result : undefined }));

      if (onError && result !== 'success') {
        onError(result);
      }

      return result;
    },
    [authState.data, authEndpoint, onError, processResponse, setAuthState],
  );

  const signup = useCallback(
    async (username: string, password: string): Promise<AuthResponseCode> => {
      let result: AuthResponseCode = 'success';
      setAuthState((s) => ({ ...s, loading: true }));

      try {
        await axios.post(`${authEndpoint}/signup`, {
          username,
          password,
        });
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const r = processResponse(e.response);
          if (r.code !== 'success') {
            result = r.code ?? 'not_authorized';
          }
        } else {
          result = 'server_error';
        }
      }

      setAuthState((s) => ({ ...s, loading: false }));

      if (onError && result !== 'success') {
        onError(result);
      }

      return result;
    },

    [authEndpoint, onError, processResponse],
  );

  const logout = useCallback(async (): Promise<void> => {
    if (!authState.data) return;
    const username = authState.data?.username;
    const token = authState.data?.token;

    try {
      // just fire and forget
      axios.post(`${authEndpoint}/logout`, { username, token }).catch(() => {});
    } catch (e) {
      /*do nothing on error*/
    }
    setAuthState({ data: null, loading: false, error: undefined });

    clearTimeout(tokenRefreshTimer);
    if (onLogout) {
      onLogout();
    }
  }, [authState.data, authEndpoint, onLogout, tokenRefreshTimer]);

  const refresh = useCallback(async () => {
    if (!authState.data?.token) {
      return;
    }
    silentLogin(authState.data.token);
  }, [authState.data, silentLogin]);

  const verifyEmail = useCallback(
    async (email: string, code: string): Promise<AuthResponseCode> => {
      let result: AuthResponseCode = 'success';
      setAuthState((s) => ({ ...s, loading: true }));
      try {
        await axios.post(`${authEndpoint}/verify-email`, { email, code });
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const r = processResponse(e.response);
          result = r.code ?? 'not_authorized';
        } else {
          result = 'server_error';
        }
      }

      if (onError && result !== 'success') {
        onError(result);
      }

      setAuthState((s) => ({ ...s, loading: false }));
      return result;
    },
    [authEndpoint, onError, processResponse],
  );

  const resendVerificationEmail = useCallback(
    async (username: string): Promise<AuthResponseCode> => {
      let result: AuthResponseCode = 'success';
      setAuthState((s) => ({ ...s, loading: true }));
      try {
        await axios.post(`${authEndpoint}/resend-verification`, { username });
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const r = processResponse(e.response);
          result = r.code ?? 'not_authorized';
        } else {
          result = 'server_error';
        }
      }
      setAuthState((s) => ({ ...s, loading: false }));
      if (onError && result !== 'success') {
        onError(result);
      }
      return result;
    },
    [authEndpoint, onError, processResponse],
  );

  useEffect(() => {
    if (!authState.data && oldToken) {
      silentLogin(oldToken);
    }
  }, [silentLogin, oldToken, authState.data]);

  const value: AuthContextData<T> = {
    user: authState.data ?? null,
    token: authState.data?.token ?? null,
    error: authState.error,
    isLoading: authState.loading,
    isSelf: (id) => id === authState.data?.id,
    isAuthenticated: !!authState.data?.token,
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    getAuthHeader: () => {
      if (!authState.data?.token) return undefined;
      return `Bearer ${authState.data.token}`;
    },
    signup,
    verifyEmail,
    resendVerificationEmail,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
